/**
 * Today — V98-3-3 (lead) · V98-3-4 (composed for every viewer). Route:
 * /w/:slug/app/today. PRD G-2, G-3; ADR-0046 §2; research §2.
 *
 * One page for everyone; its composition is derived from GET /today (the
 * viewer's hats = the union of their assignments; no role switcher):
 *
 *   VerdictBand   status + scoped counts (Org / Yours / per program /
 *                 Shared); a projected hard-date miss shows as
 *                 "Will miss · X · date" in red with Resolve (rule 23, U3)
 *   Next          ONE primary: a projected miss first, else the most
 *                 urgent item the viewer owes
 *   Owed by you   ONE urgency-sorted list, hat tag per row, each item once
 *   …             sections only for the hats the viewer holds (lead:
 *                 signatures at risk, programs on a shared scale, open by
 *                 owner, next 14 days)
 *
 * V98-3 review: no dead ends (U1), stacked owner cards on phones (U2), one
 * scope per program card (U4), one filled primary per layer (U6), solo
 * without hat tags or per-row buttons (U8), section captions removed and one
 * header style (U13), hard dates tagged in Next 14 days (U14). Controls the
 * server refuses for this viewer's role are not rendered.
 *
 * Reads: /today, /programs, /settings/team/members (names), /coverage/config,
 * and for leads /blockers (server-scoped to the caller). Writes: nudges, asks,
 * assignments (owner/admin), work-item completion, absences, delegations.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Alert, Button, PageHeader, Spinner, StatusCard, VerdictBand } from '@aigovops/design-system';
import { activeProgramKeys, computeTodayActions, programHas, type OwedRow, type ProgramCard, type TodayView } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { usePrograms } from '../lib/workspace-navigation';
import { getAuthoringSummary, getToday, listWorkspaceMembers } from '../lib/api';
import { useOptionalAuth } from '../lib/auth';
import { coverageConfig, getAlternatives, listBlockers, type IndependentPartySeatResult } from '../lib/coverage-api';
import { queryKeys } from '../lib/query-keys';
import { programTitle } from '../lib/nav-config';
import { useNarrow } from '../lib/use-narrow';
import {
  cardStatus, composeToday, datedFromBlockers, datedFromOwed, day, fmtDay, itemHref, missKeysOf, nextLabel, ownersTable, scopedReason, selfPreparedBlocked, todayCounts, todayVerdict, willMissOf,
  type Composition, type Names,
} from '../lib/today-model';
import { OwedList, WaitingList } from '../components/today/OwedList';
// V1035-4-3: ProgramCards replaced by StatusCard from DS for plain program cards.
// import { ProgramCards } from '../components/today/ProgramCards';
import { OwnersTable } from '../components/today/OwnersTable';
import { NextDays } from '../components/today/NextDays';
import { AtRiskCard } from '../components/today/AtRiskCard';
import { useAnnounce, useFocusAfter } from '../components/today/shared';
import { SignQueue, SignedToday } from '../components/today/SignQueue';
import { AdvisorDialog, SoloCard } from '../components/today/SoloCard';
import { LegalCard, inForceGaps } from '../components/today/LegalCard';
import { CmmcProgramCard, CmmcTodayItems, todayItemsOf } from '../components/today/CmmcCards';
import { FedrampGate, GovrampTodayCard } from '../components/today/GovrampCards';
import { rollupHeadline, useRollupsFor } from '../components/sweep/ProgramRollup';
import { getProgramManifest } from '@aigovops/core';
import { isScoredProgram, useCmmcCards } from '../lib/use-cmmc';
import { AwayNow, DelegateBanner, OutOfOffice, PendingAbsence, UpcomingAbsences, WhileYouWereOut, absenceState, useAbsences } from '../components/today/CoverageCards';
// V108-6-5: program coverage on the cards + the shared Next step strip (one count source with Setup).
import { NextStepStrip, type EvidenceAsk } from '../components/setup/NextStepStrip';
import { TodayNotices } from '../components/today/Notices';
import { SweepNotice, SweepToast } from '../components/sweep/SweepNotice';
import { ProgramCoverageBlock, StatusPill, programShort, statusFromCard } from '../components/setup/parts';
import { usePeople, useRefreshSetup, useSetupData, setupKeys } from '../components/setup/use-setup';
import { useSetupToast } from '../components/setup/feedback';
import { getNoSource, type CoverageProgram } from '../lib/setup-api';
import { coverageParts, stripModel, todayPrimary, weeklyDelta, deltaText } from '../lib/setup-model';
import '../components/setup/setup.css';
import './today.css';
// V106-1-8 / V106-5-6: SOC 2 (after its switch) and ISO 42001 cards from GET /today/programs (D8).
import { getTodayPrograms } from '../lib/program-screens-api';
import { rankTodayCards, todayCard } from '../lib/program-screens-model';
import { ObjectiveTodayCard } from '../components/today/ObjectiveCards';
// V117-1-10: contested laws waiting on an owner / admin decision.
import { DecisionsTodayItem } from '../components/ai-program/ContestedDecisions';
import { ProgramSetupStep, programSetupStepOf } from '../components/today/ProgramSetupStep';
import type { EnablerItem } from '../lib/setup-api';

export function useNames(slug: string): Names {
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  return useMemo(() => new Map((members.data?.members ?? []).map((m) => [m.user_id, m.display_name || m.email])), [members.data]);
}

export function Today() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 30_000 });
  const programs = usePrograms(slug);
  const names = useNames(slug);

  if (today.isLoading) return <div className="gp-today"><Spinner label="Loading Today" /></div>;
  if (today.error || !today.data) {
    return (
      <div className="gp-today">
        <PageHeader variant="app" title="Today" headingId="today-h1" />
        <Alert variant="error"><p>Today could not load.</p><Button onClick={() => void today.refetch()}>Retry</Button></Alert>
      </div>
    );
  }
  return <TodayBody view={today.data} cards={programs.data?.programs ?? []} names={names} slug={slug} prefix={prefix} workspaceName={workspace.name} />;
}

/**
 * One-approver signal from real routing state: the viewer's first blocked
 * self-prepared signature has no other approver, nobody away who comes back,
 * and nobody to ask. An access review of your own access always needs an
 * independent party. Null while unknown.
 */
function useNoOtherApprover(slug: string, owed: readonly OwedRow[], enabled: boolean): boolean | null {
  const blocked = selfPreparedBlocked(owed);
  const sig = blocked.find((r) => r.ref.kind === 'signature_request');
  const alt = useQuery({
    queryKey: ['alternatives', slug, 'signature_request', sig?.ref.id ?? ''],
    queryFn: () => getAlternatives(slug, 'signature_request', sig!.ref.id),
    enabled: enabled && !!sig, retry: false, staleTime: 60_000,
  });
  if (!blocked.length) return null;
  if (!sig) return true;
  const a = alt.data?.alternatives;
  if (!a) return null;
  return !a.options.other_approver.available && !a.options.hold.applicable && !a.options.ask_delegate.applicable;
}

export function TodayBody({ view, cards, names, slug, prefix, workspaceName }: {
  view: TodayView; cards: readonly ProgramCard[]; names: Names; slug: string; prefix: string; workspaceName: string;
}) {
  const { role } = useWorkspace();
  const canManage = role === 'owner' || role === 'admin';
  const narrow = useNarrow();
  const config = useQuery({ queryKey: ['coverage-config', slug], queryFn: () => coverageConfig(slug), staleTime: 60_000, retry: false });
  const parties = config.data?.independent_parties ?? [];
  const activeParties = config.data ? parties.filter((p) => p.state === 'active').length : null;
  const noOtherApprover = useNoOtherApprover(slug, view.owed, activeParties === 0);
  const comp: Composition = composeToday(view, { names, independentParties: activeParties, noOtherApprover });
  const todayDay = day(view.generated_at);
  const owedKeys = useMemo(() => new Set(view.owed.map((r) => r.key)), [view.owed]);
  const canonical = useMemo(() => new Map(view.owed.map((r) => [r.key, r.title])), [view.owed]);
  const programNames = useMemo(() => new Map(cards.map((c) => [c.profile_id, programTitle(c.program, c.report_type)])), [cards]);
  const blockers = useQuery({ queryKey: ['blockers', slug], queryFn: () => listBlockers(slug), enabled: comp.isLead, staleTime: 30_000, retry: false });
  const focusLater = useFocusAfter();
  const [announceNode, announce] = useAnnounce();
  const absences = useAbsences(slug, view.viewer.user_id);
  const abs = absenceState(absences.data?.absences ?? [], view.viewer.user_id, todayDay);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState<IndependentPartySeatResult | null>(null);
  const advisorOpener = useRef<string | null>(null);
  const onDone = (msg: string, focusId: string) => { focusLater(focusId); announce(msg); };
  const openAdvisor = (opener: HTMLElement) => { advisorOpener.current = opener.id || 'solo-h'; setAdvisorOpen(true); };
  // The Legal card follows the program that holds legal applicability determinations (AI Regulatory).
  const aiCard = cards.find((c) => programHas(c.program, (m) => m.features.legalDeterminations)) ?? null;
  // V102-8-1: scored programs (CMMC) get their own card (score + verdict + the scorecard's primary), not the blocker bar.
  // V103-3-6: GovRAMP leads with its own status card (the program page's), not the blocker bar.
  const isGovramp = (c: ProgramCard) => programHas(c.program, (m) => m.scoring.engine === 'govramp-snapshot');
  const govCards = cards.filter(isGovramp);
  // V106-1-8 / V106-5-6: the objective-engine cards (SOC 2 once switched, ISO 42001) replace their plain card.
  const objectiveQ = useQuery({ queryKey: ['today-programs', slug], queryFn: () => getTodayPrograms(slug), enabled: cards.some((c) => c.program === 'soc2' || c.program === 'iso-42001'), retry: false, staleTime: 30_000 });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  const adminToAsk = (members.data?.members ?? []).find((m) => (m.role === 'owner' || m.role === 'admin') && m.user_id !== view.viewer.user_id) ?? null;
  const objectiveCards = rankTodayCards(objectiveQ.data ?? []).map((oc) => {
    const pc = cards.find((c) => c.profile_id === oc.profile_id);
    const model = pc ? todayCard(oc, {
      prefix, today: todayDay, title: programTitle(pc.program, pc.report_type), isAdmin: canManage, names,
      askAdmin: adminToAsk ? { user_id: adminToAsk.user_id, name: adminToAsk.display_name || adminToAsk.email } : null,
    }) : null;
    return { oc, model };
  }).filter((x): x is { oc: (typeof x)['oc']; model: NonNullable<(typeof x)['model']> } => !!x.model);
  const objectiveIds = new Set(objectiveCards.map((x) => x.oc.profile_id));
  const plainCards = cards.filter((c) => !isScoredProgram(c.program) && !isGovramp(c) && !objectiveIds.has(c.profile_id));
  // One model per scored program (the scorecard's): its Today rows, its card and its share of "need attention".
  const cmmc = useCmmcCards(slug, prefix, cards);
  const cmmcItems = comp.sections.includes('programs') ? todayItemsOf(cmmc) : [];
  const cmmcRed = cmmcItems.some((i) => i.tone === 'red');
  const cmmcHide = new Set(cmmcItems.map((i) => i.action.href));
  const leads = new Set(view.viewer.lead_program_ids);
  const cmmcCounted = cmmc.filter((s) => s.model && (view.counts.scope === 'org' || leads.has(s.card.profile_id)));

  // V108-6-5: setup coverage + the Next step strip. ONE filled primary on the page (todayPrimary).
  const setup = useSetupData(slug);
  const refreshSetup = useRefreshSetup(slug);
  const people = usePeople(slug);
  const [setupToastNode, setupToast] = useSetupToast();
  const setupList = setup.enablers.data?.enablers ?? [];
  const covOf = new Map((setup.coverage.data?.programs ?? []).filter((c) => c.objectives_in_scope > 0).map((c) => [c.program, c]));
  const viewerRole = role ?? 'viewer';
  const strip = stripModel(setupList, { user_id: view.viewer.user_id, role: viewerRole }, slug);
  const canAssignSteps = canManage || comp.isLead;
  const showStrip = !!(strip.status || strip.next) && (strip.approver || canAssignSteps || strip.status?.owner === view.viewer.user_id);

  // BU-3: once a sweep exists, a sweep program's card takes its status and count from the roll-up (the
  // program page's own words); ConMon / coverage stay as secondary lines.
  const rollups = useRollupsFor(slug, cards).byProgram;
  const headOf = (c: ProgramCard) => { const d = rollups.get(c.program); const m = getProgramManifest(c.program); return d && m ? rollupHeadline(m, d) : null; };

  // AC-2: at-risk programs whose remaining objectives need evidence a person adds.
  const atRiskCov = cards.filter((c) => covOf.has(c.program) && ['at_risk', 'off_track'].includes(statusFromCard(c, covOf.get(c.program), todayDay)?.status ?? '') && (covOf.get(c.program)!.manual_only > 0));
  const noSourceQs = useQueries({ queries: atRiskCov.map((c) => ({ queryKey: setupKeys.noSource(slug, c.program), queryFn: () => getNoSource(slug, c.program), retry: false, staleTime: 30_000 })) });
  const noSourceOf = new Map(atRiskCov.map((c, i) => [c.program, noSourceQs[i]?.data?.no_source ?? null]));
  const evidenceAsks: EvidenceAsk[] = canAssignSteps ? atRiskCov.flatMap((c) => { const ns = noSourceOf.get(c.program); return ns && ns.total - ns.asked > 0 ? [{ program: c.program, label: programShort(c.program), count: ns.total - ns.asked }] : []; }) : [];

  const verdict = todayVerdict(view, cards);
  // R3-U7: the reason under the title is counted in the verdict's own scope (org / your programs / yours).
  const reason = scopedReason(view, cards, { total: cmmcCounted.length, bad: cmmcCounted.filter((s) => s.model!.attention).length });
  const miss = willMissOf(view);
  // Flow H (round 2): GovRAMP holds the fill only when no SOC 2 / ISO card is worse off than it.
  const objTop = objectiveCards.reduce((w, x) => Math.max(w, x.oc.status === 'off_track' ? 2 : x.oc.status === 'at_risk' || x.oc.status === 'needs_setup' ? 1 : 0), -1);
  const govTop = govCards.reduce((w, c) => Math.max(w, RANK[cardStatus(c)] ?? 0), -1);
  const holder = todayPrimary({ cmmcRed, objectiveRed: objectiveCards.some((x) => x.oc.variant === 'operate_problem'), miss: !!miss && !!comp.next, other: !!comp.solo || comp.toSign.length > 0, strip: showStrip ? strip.primary : null, gov: comp.sections.includes('programs') && govCards.length > 0 && objTop <= govTop, next: !!comp.next });
  const fixFirst = holder === 'strip' && strip.primary === 'status';
  const stripNode = showStrip || evidenceAsks.length ? <NextStepStrip slug={slug} prefix={prefix} model={showStrip ? strip : { ...strip, status: null, others: [], next: null }} people={people}
    viewer={{ user_id: view.viewer.user_id, role: viewerRole }} canAssign={canAssignSteps} filled={holder === 'strip'} evidenceAsks={evidenceAsks}
    onChanged={refreshSetup} toast={setupToast} announce={announce} /> : null;

  const nhd = view.next_hard_date.item;
  const dated = comp.isLead && blockers.data ? datedFromBlockers(blockers.data, owedKeys, canonical) : datedFromOwed(view.owed);
  const owedOverdue = comp.owedList.filter((r) => r.overdue).length;
  // Owners other than the viewer — the table is noise for a one-owner org (U8).
  const otherOwners = new Set((blockers.data ?? []).flatMap((b) => (b.holder_ids.length ? b.holder_ids : [''])).filter((u) => u !== view.viewer.user_id));
  const misses = view.next_hard_date.misses.map((m) => ({ key: m.key, hard_date: m.hard_date, user_id: m.user_id }));
  // R3-U6: the table only shows when an owner needs attention (overdue, a projected miss, or unowned work);
  // an all-on-track table is noise — Next 14 days already lists what's coming.
  const ownersNeed = blockers.data ? ownersTable(blockers.data, view.viewer.user_id, owedKeys, misses).attention.length > 0 : false;
  const showOwners = comp.sections.includes('owners') && (blockers.isLoading || !!blockers.error
    || ((otherOwners.size > 1 || (otherOwners.size === 1 && !comp.solo)) && ownersNeed));

  const pending = abs.pending ? (
    <PendingAbsence slug={slug} absence={abs.pending} names={names} prefix={prefix} signBefore={view.sign_before_leaving} compact={narrow} onDone={onDone} />
  ) : null;
  const signSection = comp.toSign.length ? (
    <section className="gp-sec" aria-labelledby="sign-h">
      <div className="gp-sec-head"><h2 id="sign-h" className="gp-sec-h">To sign</h2><span className="gp-sec-count">Yours <b>{comp.toSign.length}</b></span></div>
      <SignQueue rows={comp.toSign} slug={slug} prefix={prefix} names={names} todayDay={todayDay} programNames={programNames} missKeys={missKeysOf(view)} onDone={onDone} />
    </section>
  ) : null;

  return (
    <div className="gp-today">
      {announceNode}
      <PageHeader variant="app" title="Today" headingId="today-h1" subtitle={[fmtDay(view.generated_at, true), workspaceName, reason].filter(Boolean).join(' · ')} />

      <VerdictBand
        verdict={verdict}
        subject="Today"
        counts={todayCounts(view, cards)}
        willMiss={miss ? {
          title: miss.title, date: fmtDay(miss.date), dateTime: miss.date, why: miss.why,
          action: miss.ref ? <Link className="gp-band-act" to={itemHref(prefix, miss.ref)} aria-label={`Resolve · ${miss.title}`}>Resolve</Link> : undefined,
        } : undefined}
        nextHardDate={!miss && nhd ? { label: `Next hard date · ${nhd.title}`, date: fmtDay(nhd.hard_date), dateTime: nhd.hard_date } : undefined}
      />

      {/* G7-H3: a CMMC deadline under 14 days (Level 1 lapse, POA&M closeout) sits above everything else, with its fix. */}
      <CmmcTodayItems items={cmmcItems} />

      {/* Phone: what to sign comes first; the pending out-of-office folds to one row under it (U3). */}
      {narrow ? null : pending}
      {abs.away ? <AwayNow slug={slug} absence={abs.away} onDone={onDone} /> : null}
      {/* R3-S6: your confirmed upcoming out of office — Edit, Cancel and what to sign before you leave. */}
      <UpcomingAbsences slug={slug} rows={view.upcoming_absences ?? []} prefix={prefix} todayDay={todayDay} onDone={onDone} />
      {abs.back ? <WhileYouWereOut slug={slug} absenceId={abs.back.absence_id} names={names} prefix={prefix} onDone={onDone} /> : null}
      {comp.sections.includes('delegate_banner') ? <DelegateBanner slug={slug} names={names} count={view.owed.filter((r) => r.on_behalf_of_user_id).length} /> : null}
      {comp.solo ? (
        <SoloCard rows={view.owed.filter((r) => comp.solo!.keys.includes(r.key))} prefix={prefix} slug={slug} parties={parties.filter((p) => p.state !== 'active')}
          lastInvite={lastInvite} canManage={canManage} onAddAdvisor={openAdvisor}
          onInvite={(r, m) => { setLastInvite(r); onDone(m, `seat-${r.party_id}`); }} />
      ) : null}

      {/* Build review (glance H1): a failed source that holds the page's primary sits right under the band, not below the cards. */}
      {fixFirst ? stripNode : null}
      {/* V109-1-19 R3 (Medium): the viewer's unread notices (a source that stopped collecting, …), each linking to its fix. */}
      <TodayNotices slug={slug} announce={announce} />
      {/* V109-5-2 / 5-4: the latest sweep — every count links to its own view, program-aware (≤ 2 clicks to each view). */}
      <SweepNotice slug={slug} prefix={prefix} />
      {/* V117-1-10: a contested law counts toward nothing until an owner or admin decides; they see it here. */}
      {canManage ? <DecisionsTodayItem slug={slug} /> : null}
      {comp.next ? (
        // One filled primary per surface (V108-6-5 todayPrimary): a red CMMC deadline row, a projected miss, the
        // setup strip's fix for a failed step, GovRAMP's status card, the setup strip's next step, then Next.
        <Link className={`gp-linkbtn${holder === 'next' || holder === 'miss' ? ' btn-primary' : ''} gp-next`} to={itemHref(prefix, comp.next.ref)}>{nextLabel(comp.next, todayDay, miss)}</Link>
      ) : null}

      {comp.sections.includes('at_risk') && view.signatures_at_risk ? (
        <section className="gp-sec" aria-labelledby="risk-h">
          <div className="gp-sec-head"><h2 id="risk-h" className="gp-sec-h">Signatures at risk</h2></div>
          <AtRiskCard rows={view.signatures_at_risk} names={names} slug={slug} prefix={prefix} focusLater={focusLater} announce={announce} />
        </section>
      ) : null}

      {signSection}
      {narrow ? pending : null}

      <section id="owed" className="gp-sec" aria-labelledby="owed-h" tabIndex={-1}>
        <div className="gp-sec-head">
          <h2 id="owed-h" className="gp-sec-h" tabIndex={-1}>{comp.toSign.length ? 'To provide' : 'Owed by you'}</h2>
          {/* V98-4 review T4-H1: the header counts the rows shown. */}
          <span className="gp-sec-count">Yours <b>{comp.owedList.length}</b>{owedOverdue ? <> · <b>{owedOverdue}</b> overdue</> : null}</span>
        </div>
        <OwedList rows={comp.owedList} names={names} prefix={prefix} slug={slug} todayDay={todayDay} programNames={programNames} solo={!!comp.solo}
          showHatFilter={comp.showHatFilter} label={comp.toSign.length ? 'To provide' : 'Owed by you'} onDone={onDone} />
      </section>

      {comp.sections.includes('signed_today') ? <SignedToday slug={slug} viewerId={view.viewer.user_id} names={names} onDone={onDone} /> : null}

      {comp.sections.includes('waiting') ? (
        <section className="gp-sec" aria-labelledby="wait-h">
          <div className="gp-sec-head"><h2 id="wait-h" className="gp-sec-h">Waiting on others</h2><span className="gp-sec-count">Yours <b>{comp.waiting.length}</b></span></div>
          <WaitingList rows={comp.waiting} names={names} prefix={prefix} todayDay={todayDay} />
        </section>
      ) : null}

      {comp.sections.includes('legal') ? (
        <LegalCard ai={aiCard} gaps={inForceGaps(aiCard?.profile_id ?? null, view.owed, blockers.data)} prefix={prefix} todayDay={todayDay} slug={slug}
          canNudge={comp.isLead} focusLater={focusLater} announce={announce} summary={view.ai_legal ?? null} />
      ) : null}

      {comp.sections.includes('programs') && cards.length ? (() => {
        // V1035-4-3 / ADR-0071: compute next actions and determine which card gets primary.
        // At-risk first in the DOM (no CSS order); coverage cards rank by the status their pill shows.
        // The status each card shows: the roll-up's (BU-3), else a coverage card's pill, else the card's own.
        const shownStatus = (c: ProgramCard): string => headOf(c)?.status.tone ?? (covOf.has(c.program) ? (statusFromCard(c, covOf.get(c.program), todayDay)?.status ?? cardStatus(c)) : cardStatus(c));
        const pillRank = (c: ProgramCard) => RANK[shownStatus(c) as keyof typeof RANK] ?? 0;
        const allCards = [...plainCards].sort((a, b) => pillRank(b) - pillRank(a));
        const todayActions = computeTodayActions(allCards, slug, activeProgramKeys());
        // The top objective card fills its button unless a plain card ranks higher (off track beats at risk).
        const topObjective = objectiveCards[0]?.oc ?? null;
        const topPlain = allCards[0] ? pillRank(allCards[0]) : -1;
        const objWeight = topObjective ? (topObjective.status === 'off_track' ? 2 : topObjective.status === 'at_risk' || topObjective.status === 'needs_setup' ? 1 : 0) : -1;
        const objectiveFirst = !!topObjective && objWeight >= topPlain;
        // V108-8-16: each program card names its own next setup step as a quiet link (one filled primary stays put).
        // Glance H (V108-8-16 review): never the same step twice — the strip's own step stays in the strip.
        const stripStepIds = new Set([strip.next?.id, strip.status?.step.id].filter((x): x is string => !!x && showStrip));
        // Each card names its next step the strip doesn't already show (FedRAMP: "Add Claims triage to FedRAMP").
        const cardSteps = setupList.filter((e) => !stripStepIds.has(e.id));
        const setupOf = (program: string) => (programSetupStepOf(program, cardSteps) ? <ProgramSetupStep program={program} enablers={cardSteps} prefix={prefix} /> : null);
        const objectiveNodes = objectiveCards.map(({ oc, model }, i) => (
          <li key={oc.profile_id} className="v6-today-li" data-program={oc.program}>
            <ObjectiveTodayCard card={oc} model={model} slug={slug} prefix={prefix} primary={i === 0 && (holder === 'objective' || (!holder && objectiveFirst))}
              email={adminToAsk?.email ?? null} onAsked={announce} setupStep={setupOf(oc.program)} />
          </li>
        ));
        const statusCounts = { on_track: 0, at_risk: 0, off_track: 0 };
        // The header counts the status each card shows: objective cards (v10.6) their own status; the
        // rest the roll-up's (BU-3), else the coverage pill, else the card's own.
        for (const x of objectiveCards) { const s = x.oc.status === 'off_track' ? 'off_track' : x.oc.status === 'on_track' ? 'on_track' : 'at_risk'; statusCounts[s]++; }
        for (const c of cards.filter((k) => !objectiveIds.has(k.profile_id))) { const s = shownStatus(c); if (s in statusCounts) statusCounts[s as keyof typeof statusCounts]++; }

        return (
          <section className="gp-sec" aria-labelledby="programs-h">
            <div className="gp-sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <h2 id="programs-h" className="gp-sec-h">Programs</h2>
              {/* V1035-4-3 / R1-F7: segment count summary */}
              <div style={{ display: 'flex', gap: '12px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                {statusCounts.on_track > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--status-on-track-dot, var(--success))' }} />{statusCounts.on_track} on track</span>}
                {statusCounts.at_risk > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--status-at-risk-dot, var(--warning))' }} />{statusCounts.at_risk} at risk</span>}
                {statusCounts.off_track > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--status-off-track-dot, var(--critical))' }} />{statusCounts.off_track} off track</span>}
              </div>
            </div>

            {/* V1035-4-3: all program cards in one list (preserves test selectors: ul[aria-label="Programs"] > li) */}
            <ul role="list" className="gp-cards gp-plain-list" aria-label="Programs" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {/* GovRAMP leads with its own status-first card */}
              {/* V106-1-8 / V106-5-6 (D8): SOC 2 / ISO 42001 come first when they rank first; only the top-ranked card fills its button. */}
              {objectiveFirst ? objectiveNodes : null}
              {govCards.map((c) => <GovrampTodayCard key={c.profile_id} card={c} prefix={prefix} slug={slug} primary={holder === 'gov'} rollup={rollups.get(c.program) ?? null} setupStep={setupOf(c.program)} />)}
              {objectiveFirst ? null : objectiveNodes}
              {allCards.map((c) => {
                const action = todayActions.actions.find((a) => a.program === c.program);
                const isPrimary = todayActions.primaryProgram === c.program;
                const head = headOf(c);
                const status = (head?.status.tone ?? cardStatus(c)) as 'on_track' | 'at_risk' | 'off_track';
                const title = programTitle(c.program, c.report_type);
                const metricPct = head ? (head.applicable ? Math.round((head.met / head.applicable) * 100) : 100)
                  : c.blockers.total > 0 ? Math.round(((c.blockers.total - c.blockers.overdue) / c.blockers.total) * 100) : 100;
                const rollMetric = head ? <RollupMetric head={head} /> : null;

                // V103-6-3: FedRAMP Class A gate wraps the card
                if (c.program === 'fedramp-cr26' && c.certification_class === 'A') {
                  const cardEl = (
                    <li key={c.profile_id}>
                      <StatusCard
                        name={title}
                        status={status}
                        metric={rollMetric ?? <>{c.blockers.overdue > 0 ? `${c.blockers.overdue} overdue` : `${c.blockers.total} items`}</>}
                        metricPct={metricPct}
                        date={c.next_date ? `${c.next_date.what} ${fmtDay(c.next_date.date)}` : null}
                        href={`${prefix}/programs/${c.program}`}
                        action={action ? { label: action.action.label, href: action.action.href, danger: status === 'off_track' } : { label: 'Open program', href: `${prefix}/programs/${c.program}` }}
                        primary={isPrimary && !holder && !objectiveFirst}
                        nextStep={setupOf(c.program)}
                      />
                    </li>
                  );
                  return <FedrampGate key={c.profile_id} fr={c} gov={govCards[0] ?? null} prefix={prefix} slug={slug} fallback={cardEl} setupStep={setupOf(c.program)} />;
                }

                const cov = covOf.get(c.program);
                if (cov) return <li key={c.profile_id}><CoverageCard card={c} cov={cov} title={title} prefix={prefix} todayDay={todayDay} enablers={setupList} cardSteps={cardSteps} noSource={noSourceOf.get(c.program) ?? null} head={head} /></li>;
                return (
                  <li key={c.profile_id}>
                    <StatusCard
                      name={title}
                      status={status}
                      metric={rollMetric ?? <>{c.blockers.overdue > 0 ? `${c.blockers.overdue} overdue` : `${c.blockers.total} open ${c.blockers.total === 1 ? 'item' : 'items'}`}</>}
                      metricPct={metricPct}
                      date={c.next_date ? `${c.next_date.what} ${fmtDay(c.next_date.date)}` : null}
                      href={`${prefix}/programs/${c.program}`}
                      action={action ? { label: action.action.label, href: action.action.href, danger: status === 'off_track' } : { label: 'Open program', href: `${prefix}/programs/${c.program}` }}
                      primary={isPrimary && !holder && !objectiveFirst}
                      nextStep={setupOf(c.program)}
                    />
                  </li>
                );
              })}

              {/* CMMC cards (scored) retain their specialized rendering */}
              {cmmc.map((st) => <CmmcProgramCard key={st.card.profile_id} state={st} hideHrefs={cmmcHide} />)}
            </ul>
            {/* V108-6-5: the one shared Next step strip under the cards. */}
            {fixFirst ? null : stripNode}
          </section>
        );
      })() : null}

      {showOwners ? (
        <section id="owners" className="gp-sec" aria-labelledby="owners-h" tabIndex={-1}>
          <div className="gp-sec-head">
            <h2 id="owners-h" className="gp-sec-h" tabIndex={-1}>Open by owner</h2>
            <span className="gp-sec-count">{view.counts.scope === 'org' ? 'Org' : 'Your programs'}</span>
          </div>
          {blockers.isLoading ? <Spinner label="Loading owners" />
            : blockers.error ? <Alert variant="error"><p>Owners could not load.</p><Button onClick={() => void blockers.refetch()}>Retry</Button></Alert>
            : <OwnersTable rows={blockers.data ?? []} viewerId={view.viewer.user_id} owedKeys={owedKeys} misses={misses} prefix={prefix} slug={slug} todayDay={todayDay}
                canAssign={canManage} names={names} focusLater={focusLater} announce={announce} />}
        </section>
      ) : null}

      <NeedsAuthoringCount prefix={prefix} />

      <section className="gp-sec" aria-labelledby="next14-h">
        <div className="gp-sec-head">
          <h2 id="next14-h" className="gp-sec-h">Next 14 days</h2>
          <Link className="gp-sec-count" to={`${prefix}/calendar`}>Full calendar</Link>
        </div>
        <NextDays items={dated} todayDay={todayDay} prefix={prefix} isLead={comp.isLead} />
      </section>

      {!abs.pending && !abs.away ? <OutOfOffice slug={slug} viewerId={view.viewer.user_id} names={names} todayDay={todayDay} onDone={onDone} /> : null}
      {setupToastNode}
      {setupToastNode ? null : <SweepToast slug={slug} prefix={prefix} />}
      {comp.solo && canManage ? (
        <AdvisorDialog open={advisorOpen} slug={slug} clears={comp.solo.count} onClose={() => { setAdvisorOpen(false); focusLater(advisorOpener.current ?? 'solo-h'); }}
          onDone={(m, r) => { setAdvisorOpen(false); setLastInvite(r); onDone(m, `seat-${r.party_id}`); }} />
      ) : null}
    </div>
  );
}

const RANK = { on_track: 0, at_risk: 1, off_track: 2 } as const;

type RollupHead = ReturnType<typeof rollupHeadline>;
/** BU-3: "Core · 26 of 44 controls met · 18 to go" — the roll-up's own words on a Today card. */
function RollupMetric({ head }: { head: RollupHead }) {
  const [label] = head.text.split(' · ');
  return (
    <span className="gp-roll-metric" data-part="rollup-metric">
      <span className="gp-roll-metric-k">{`${label} · `}</span>{head.met} <span className="gp-roll-metric-of">{`of ${head.applicable} ${head.noun} met`}</span>
      <span className="gp-roll-metric-go">{`${head.toGo} to go`}</span>
    </span>
  );
}

/**
 * V108-6-5: a program's Today card when it has coverage — status pill (+ popover), "N of M seen", the delta,
 * the four-part bar, the projection and "See <program> steps →". The page's filled primary lives in the strip,
 * so the card's action is a quiet link (ADR-0071 amendment). AC-2: an at-risk card's no-source cause line.
 */
function CoverageCard({ card, cov, title, prefix, todayDay, enablers, cardSteps, noSource, head = null }: {
  card: ProgramCard; cov: CoverageProgram; title: string; prefix: string; todayDay: string; enablers: Parameters<typeof coverageParts>[1];
  /** V108-8-16: the steps the shared strip doesn't already show (never the same step twice on Today). */
  cardSteps: readonly EnablerItem[];
  noSource: { total: number; owners: number; asked: number } | null;
  /** BU-3: with a sweep, the status and the headline count are the roll-up's; coverage stays below as the next step. */
  head?: RollupHead | null;
}) {
  const own = statusFromCard(card, cov, todayDay) ?? { status: 'on_track' as const, date: null, dateWhat: null };
  const st = head ? { ...own, status: head.status.tone } : own;
  const parts = coverageParts(cov, enablers);
  const delta = weeklyDelta(cov, todayDay);
  const short = programShort(card.program);
  const atRisk = st.status === 'at_risk' || st.status === 'off_track';
  const causeLine = atRisk && parts.noSource > 0 ? (
    <p className="su-cause" data-part="no-source-cause"><Link to={`${prefix}/setup?program=${card.program}#nosrc-${card.program}`}>{parts.noSource} need evidence you add <span aria-hidden="true">→</span></Link>
      {noSource ? <span className="su-muted">({noSource.owners} {noSource.owners === 1 ? 'owner' : 'owners'} · {noSource.asked} asked)</span> : null}</p>
  ) : undefined;
  const period = card.periods?.find((p) => p.active) ?? card.periods?.[0];
  const stage = period?.stage;
  return (
    <StatusCard
      name={title}
      status={st.status}
      eyebrow={stage ? stage.charAt(0).toUpperCase() + stage.slice(1) : undefined}
      metric={head ? <RollupMetric head={head} /> : <>{parts.seen} <span style={{ fontSize: 'var(--fs-md)', fontWeight: 500, color: 'var(--text-muted)' }}>of {parts.total} seen</span><span style={{ fontSize: 'var(--fs-body)', fontWeight: 500, color: 'var(--text-dim)' }}>{parts.togo} to go</span></>}
      metricPct={head ? (head.applicable ? Math.round((head.met / head.applicable) * 100) : 100) : undefined}
      deltaText={head ? undefined : deltaText(delta) ?? undefined}
      date={st.date ? `${st.dateWhat ?? 'Next date'} ${fmtDay(st.date)}` : null}
      hideDate
      href={`${prefix}/programs/${card.program}`}
      aria-label={head ? `${title} — ${head.text}, ${head.status.text}` : `${title} — ${parts.seen} of ${parts.total} seen`}
      statusControl={<StatusPill name={short} status={st} parts={parts} delta={delta} today={todayDay} programHref={`${prefix}/programs/${card.program}`} />}
      nextStep={<ProgramCoverageBlock cov={cov} enablers={enablers} status={st} today={todayDay} prefix={prefix} heading={null} noSourceHref={`${prefix}/setup?program=${card.program}#nosrc-${card.program}`} causeLine={causeLine} showProjection hideNumber />}
      // V108-8-16: the card's quiet action names the program's own next setup step (the same ranked step as Setup).
      action={(() => { const st = programSetupStepOf(card.program, cardSteps); return st
        ? { label: `Setup: ${st.label} →`, href: `${prefix}/setup?step=${encodeURIComponent(st.step.id)}#steps`, variant: 'link' as const }
        : { label: `See ${short} steps →`, href: `${prefix}/setup?program=${card.program}#steps`, variant: 'link' as const }; })()}
    />
  );
}

/**
 * v10.4 Track G (V104-G-7): for a site Editor or Admin, how many laws need a
 * rule written by hand, linking to the queue's Needs authoring lane. Hidden
 * when there are none (no empty card) and for everyone else.
 */
export function NeedsAuthoringCount({ prefix }: { prefix: string }) {
  const user = useOptionalAuth()?.user;
  const editor = user?.role === 'editor' || user?.role === 'admin';
  const summary = useQuery({ queryKey: queryKeys.authoringSummary(), queryFn: () => getAuthoringSummary(), enabled: editor, retry: false, staleTime: 60_000 });
  // Only what an editor can act on now; items waiting for a rule field are not counted.
  const n = summary.data?.open ?? 0;
  if (!editor || n === 0) return null;
  return (
    <section className="gp-sec" aria-labelledby="authoring-today-h">
      <div className="gp-sec-head">
        <h2 id="authoring-today-h" className="gp-sec-h">Needs authoring</h2>
        <span className="gp-sec-count">Laws <b>{n}</b></span>
      </div>
      <Link className="gp-linkbtn" to={`${prefix}/queue?lane=authoring`}>
        {n === 1 ? 'Write the rule for 1 law' : `Write rules for ${n} laws`}
      </Link>
    </section>
  );
}
